import { useState, useMemo, useCallback } from 'react'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig<T> {
  key: keyof T | string
  direction: SortDirection
}

export interface UseSortableTableResult<T> {
  sortConfig: SortConfig<T> | null
  sortedData: T[]
  requestSort: (key: keyof T | string) => void
  getSortDirection: (key: keyof T | string) => SortDirection | null
}

type SortValueExtractor<T> = (item: T, key: keyof T | string) => unknown

function defaultExtractor<T>(item: T, key: keyof T | string): unknown {
  return (item as Record<string, unknown>)[key as string]
}

function compareValues(a: unknown, b: unknown, direction: SortDirection): number {
  if (a === null || a === undefined) return direction === 'asc' ? 1 : -1
  if (b === null || b === undefined) return direction === 'asc' ? -1 : 1

  if (typeof a === 'string' && typeof b === 'string') {
    return direction === 'asc'
      ? a.localeCompare(b, undefined, { sensitivity: 'base' })
      : b.localeCompare(a, undefined, { sensitivity: 'base' })
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a
  }

  if (a instanceof Date && b instanceof Date) {
    return direction === 'asc'
      ? a.getTime() - b.getTime()
      : b.getTime() - a.getTime()
  }

  const strA = String(a)
  const strB = String(b)
  return direction === 'asc'
    ? strA.localeCompare(strB, undefined, { sensitivity: 'base' })
    : strB.localeCompare(strA, undefined, { sensitivity: 'base' })
}

export function useSortableTable<T>(
  data: T[],
  defaultSort?: SortConfig<T>,
  extractor: SortValueExtractor<T> = defaultExtractor
): UseSortableTableResult<T> {
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(defaultSort || null)

  const requestSort = useCallback((key: keyof T | string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }, [])

  const getSortDirection = useCallback(
    (key: keyof T | string): SortDirection | null => {
      if (sortConfig?.key === key) {
        return sortConfig.direction
      }
      return null
    },
    [sortConfig]
  )

  const sortedData = useMemo(() => {
    if (!sortConfig) return data

    return [...data].sort((a, b) => {
      const valueA = extractor(a, sortConfig.key)
      const valueB = extractor(b, sortConfig.key)
      return compareValues(valueA, valueB, sortConfig.direction)
    })
  }, [data, sortConfig, extractor])

  return {
    sortConfig,
    sortedData,
    requestSort,
    getSortDirection,
  }
}
